<template>
  <div v-if="canEdit" class="special-case-editor">
    <EditCreateTitleBox :label="$t('tools.special_cases.' + (mode === 'serve' ? 'serve' : 'assign'))" />
    <div v-if="errors.length" class="alert alert-danger" role="alert">
      <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
    </div>
    <div v-if="loading" class="loader-container">
      <Loader :loading="loading" size="sm"></Loader>
    </div>
    <div v-else-if="!unavailable" class="editor-layout">
      <StepsPanelWrapper
        ref="stepsWrapper"
        :steps="steps"
        :current-step.sync="currentStep"
        :step-props-fn="getStepProps"
        @step-input="onStepInput"
        @validation-error="handleValidationError"
      />
      <SpecialCaseRightPanel :value="form" :options="options" :mode="mode" :saving="saving" @save="prepareSave" @back="back" />
    </div>
    <DxPopup
      :visible.sync="allocationVisible"
      :title="$t('tools.special_cases.allocation_title')"
      width="95%"
      :max-width="480"
      height="auto"
      :show-close-button="!saving"
      :close-on-outside-click="!saving"
    >
      <div class="allocation-dialog">
        <p>{{ $t("tools.special_cases.create_confirm", { count: targetUuids.length }) }}</p>
        <DxRadioGroup
          v-if="targetUuids.length > 1"
          :value.sync="allocation"
          :data-source="allocationOptions"
          value-expr="value"
          display-expr="text"
          :disabled="saving"
        />
        <div class="allocation-actions">
          <SmagriaButton :label="$t('common.cancel')" custom-class="reset border" :disabled="saving" :action="cancelAllocation" />
          <SmagriaButton
            :label="$t('tools.special_cases.create_works')"
            custom-class="success"
            :disabled="saving"
            :action="save"
          />
        </div>
      </div>
    </DxPopup>
  </div>
  <Forbidden v-else />
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapGetters } from "vuex";
import DxPopup from "devextreme-vue/popup";
import DxRadioGroup from "devextreme-vue/radio-group";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";
import SpecialCaseRightPanel from "./partials/SpecialCaseRightPanel.vue";
import workSetupStep from "./partials/SpecialCaseWorkSetupStep.vue";
import workMeasurementsStep from "./partials/SpecialCaseWorkMeasurementsStep.vue";
import notesStep from "./partials/SpecialCaseNotesStep.vue";
import parcelStep from "./partials/SpecialCaseParcelStep.vue";
import worksStep from "./partials/SpecialCaseWorksStep.vue";
import Forbidden from "@/pages/errors/Forbidden.vue";
import Loader from "@/components/Loader";
import { permissions, permissionTypes, canEdit } from "@/helpers/Permissions";
import { successMessage, errorMessage } from "@/helpers/common";
import { SPECIAL_CASES_ENDPOINT, numericFields, durationFields } from "./fields";

export default {
  name: "ValidatedDataSpecialCaseEditor",
  middleware: ["auth"],
  components: {
    DxPopup,
    DxRadioGroup,
    SmagriaButton,
    EditCreateTitleBox,
    StepsPanelWrapper,
    SpecialCaseRightPanel,
    Forbidden,
    Loader,
  },
  data() {
    return {
      form: { target_uuids: [], target_parcels: [], target_filters: {} },
      options: {},
      numericFields,
      durationFields,
      steps: [],
      currentStep: 1,
      loading: true,
      saving: false,
      unavailable: false,
      errors: [],
      allocation: "equal",
      allocationVisible: false,
    };
  },
  computed: {
    ...mapGetters({ user: "auth/user" }),
    config() {
      return { permission: permissions.VALIDATED_DATA_SPECIAL_CASES, permission_type: permissionTypes.module };
    },
    canEdit() {
      return canEdit(this);
    },
    mode() {
      return this.$route.query.mode === "serve" ? "serve" : "assign";
    },
    targetUuids() {
      return this.form.target_uuids || [];
    },
    selectors() {
      const selectors = [
        { field: "department_uuid", label: "tools.import_validator.department", options: this.options.departments || [] },
        { field: "season_uuid", label: "common.season", options: this.options.seasons || [] },
        { field: "work_uuid", label: "common.work_name", options: this.options.works || [] },
        { field: "person_uuid", label: "tools.import_validator.person", options: this.options.persons || [] },
        {
          field: "machine_uuid",
          label: "tools.import_validator.work_machine_name",
          options: (this.options.machines || []).filter((machine) => !machine.equipment),
        },
      ];
      /*if (this.mode === "assign")
        selectors.splice(2, 0, { field: "culture_uuid", label: "common.culture", options: this.options.cultures || [] });*/
      return selectors;
    },
    equipment() {
      return (this.options.machines || []).filter((machine) => machine.equipment);
    },
    allocationOptions() {
      return ["equal", "hectare"].map((value) => ({ value, text: this.$t("tools.special_cases.allocation_" + value) }));
    },
  },
  async created() {
    this.steps = this.buildSteps();
    try {
      const [detail, lookups] = await Promise.all([
        axios.get(SPECIAL_CASES_ENDPOINT + "/" + this.$route.params.uuid),
        axios.get(SPECIAL_CASES_ENDPOINT + "/options"),
      ]);
      this.options = lookups.data.data;
      const record = detail.data.data.record;
      if (record.processed || record.deleted_at || !record.validated_data || record.validated_data.deleted_at) {
        this.unavailable = true;
        this.errors = [this.$t("tools.special_cases.unavailable")];
        return;
      }
      const source = record.validated_data;
      const dateValue = (value, format) => (value && moment(value).isValid() ? moment(value).format(format) : null);
      const find = (list, name) =>
        (list || []).find(
          (item) =>
            String(item.name || "")
              .trim()
              .toLowerCase() ===
            String(name || "")
              .trim()
              .toLowerCase(),
        )?.uuid || null;
      const machine = (this.options.machines || []).find(
        (item) =>
          !item.equipment &&
          String(item.registration_number || "").toLowerCase() === String(source.registration_number || "").toLowerCase() &&
          !!source.registration_number,
      );
      this.form = {
        department_uuid: source.department_uuid || null,
        season_uuid: source.season_uuid || null,
        // culture_uuid: find(this.options.cultures, source.culture),
        work_uuid: find(this.options.works, source.work_process),
        person_uuid: find(this.options.persons, source.person),
        machine_uuid: machine?.uuid || null,
        equipment_uuids: [],
        use_target_net_surface: false,
        target_uuids: [],
        target_parcels: [],
        target_filters: {
          date_from: dateValue(source.start_date, "YYYY-MM-DD"),
          date_to: dateValue(source.end_date, "YYYY-MM-DD"),
        },
        notes: source.notes || "",
        work_started_at: dateValue(source.start_date, "YYYY-MM-DDTHH:mm:ss"),
        work_ended_at: dateValue(source.end_date, "YYYY-MM-DDTHH:mm:ss"),
      };
      const equipmentUuid = find(this.equipment, source.work_machine_name);
      if (equipmentUuid) this.form.equipment_uuids = [equipmentUuid];
      numericFields.forEach(([field]) => this.$set(this.form, field, Number(source[field]) || 0));
      durationFields.forEach(([field]) => this.$set(this.form, field, this.minutes(source[field])));
    } catch (error) {
      this.showErrors(error);
      this.unavailable = true;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    buildSteps() {
      return [
        { number: 1, component: workSetupStep, label: this.$t("tools.special_cases.work_setup"), ref: "SpecialCaseWorkSetupStep" },
        {
          number: 2,
          component: workMeasurementsStep,
          label: this.$t("tools.special_cases.work_measurements"),
          ref: "SpecialCaseWorkMeasurementsStep",
        },
        {
          number: 3,
          component: this.mode === "assign" ? parcelStep : worksStep,
          label: this.$t(this.mode === "assign" ? "tools.special_cases.parcels_step" : "tools.special_cases.works_step"),
          ref: this.mode === "assign" ? "SpecialCaseParcelStep" : "SpecialCaseWorksStep",
        },
        { number: 4, component: notesStep, label: this.$t("common.notes"), ref: "SpecialCaseNotesStep" },
      ];
    },
    getStepProps() {
      return { formData: this.form, options: this.options, mode: this.mode, saving: this.saving };
    },
    onStepInput(step, payload) {
      if (!step || !payload) return;
      this.form = { ...this.form, ...payload };
    },
    handleValidationError(error) {
      errorMessage(this, error.message || this.$t("fill_out_error"));
    },
    back() {
      this.$router.push({ name: "validated-data-special-cases" });
    },
    minutes(value) {
      if (!value) return 0;
      const match = String(value)
        .split(" ")
        .pop()
        .match(/^(\d+):(\d{2})/);
      return match ? Number(match[1]) * 60 + Number(match[2]) : 0;
    },
    cancelAllocation() {
      this.allocationVisible = false;
    },
    prepareSave() {
      this.errors = [];
      this.selectors.forEach((selector) => {
        if (!this.form[selector.field]) this.errors.push(this.$t("tools.special_cases.required", { field: this.$t(selector.label) }));
      });
      if (!this.targetUuids.length) this.errors.push(this.$t("tools.special_cases.select_targets"));
      if (
        !this.form.work_started_at ||
        !this.form.work_ended_at ||
        !moment(this.form.work_started_at).isValid() ||
        !moment(this.form.work_ended_at).isValid() ||
        moment(this.form.work_ended_at).isBefore(this.form.work_started_at)
      )
        this.errors.push(this.$t("tools.special_cases.invalid_dates"));
      [...numericFields, ...durationFields].forEach(([field, label]) => {
        if (field === "net_surface_worked" && this.form.use_target_net_surface) return;
        if (this.form[field] === null || !Number.isFinite(this.form[field]) || this.form[field] < 0)
          this.errors.push(this.$t("tools.special_cases.required", { field: this.$t(label) }));
      });
      if (durationFields.every(([field]) => !this.form[field])) this.errors.push(this.$t("tools.special_cases.duration_required"));
      if (this.errors.length) return;
      this.allocation = "equal";
      this.allocationVisible = true;
    },
    showErrors(error) {
      const response = error.response?.data || {};
      this.errors = response.errors ? Object.values(response.errors).flat() : [response.message || this.$t("tools.special_cases.failed")];
    },
    async save() {
      if (this.saving) return;
      this.saving = true;
      try {
        const payload = { ...this.form };
        delete payload.target_works;
        if (payload.use_target_net_surface) delete payload.net_surface_worked;
        await axios.post(SPECIAL_CASES_ENDPOINT + "/" + this.$route.params.uuid + "/process", {
          ...payload,
          mode: this.mode,
          allocation: this.targetUuids.length > 1 ? this.allocation : "equal",
          target_uuids: this.targetUuids,
          work_started_at: moment(this.form.work_started_at).format("YYYY-MM-DD HH:mm:ss"),
          work_ended_at: moment(this.form.work_ended_at).format("YYYY-MM-DD HH:mm:ss"),
        });
        successMessage(this, this.$t("tools.special_cases.created", { count: this.targetUuids.length }));
        this.back();
      } catch (error) {
        this.allocationVisible = false;
        this.showErrors(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.special-case-editor {
  height: calc(100% - 70px);
}
.editor-layout {
  display: flex;
  width: 100%;
  height: 100%;
}
.allocation-dialog {
  padding: 8px;
}
.allocation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
</style>
